/**
 * `text_short` renderer — composes `@nextday/funnel-ui` `TextShort`.
 *
 * Stores typed value under `data.answerKey`. Accepts a JSON-authorable
 * preset list and an optional `randomFromList` array — the renderer wires a
 * "Random" pill that picks a value from `randomFromList` (since JSON cannot
 * carry a `randomize: () => string` callback directly).
 *
 * Step JSON shape:
 *
 *   {
 *     "type": "text_short",
 *     "data": {
 *       "question": "What should we call you?",
 *       "placeholder": "Your name",
 *       "answerKey": "display_name",
 *       "presets": ["Alex", "Sam", "Riley"],
 *       "randomFromList": ["Mira", "Luna", "Aria"],
 *       "maxLength": 40
 *     }
 *   }
 */
import { TextShort } from "@nextday/funnel-ui";
import { z } from "zod";

import { createRenderer } from "@survey-kit/registry";
import type { RendererProps } from "@survey-kit/types";

export const textShortSchema = z
  .object({
    question: z.string().min(1),
    description: z.string().optional(),
    placeholder: z.string().optional(),
    answerKey: z.string().min(1),
    presets: z.array(z.string().min(1)).optional(),
    /**
     * When present, the renderer adds a "Random" preset pill that picks a
     * value uniformly from this list. Requires `presets` because the
     * underlying funnel-ui `TextShort` only renders the pill row (which
     * hosts the Random pill) when `presets` is non-empty — supplying
     * `randomFromList` without `presets` would silently no-op.
     */
    randomFromList: z.array(z.string().min(1)).optional(),
    maxLength: z.number().int().positive().optional(),
  })
  .strict()
  .refine((value) => !value.randomFromList?.length || (value.presets?.length ?? 0) > 0, {
    message:
      "`randomFromList` requires a non-empty `presets` array (the Random pill renders inside the presets row).",
    path: ["randomFromList"],
  });

export type TextShortData = z.infer<typeof textShortSchema>;

const buildRandomizer = (pool: ReadonlyArray<string> | undefined): (() => string) | undefined => {
  if (!pool || pool.length === 0) return undefined;
  return () => {
    const index = Math.floor(Math.random() * pool.length);
    return pool[index] ?? pool[0]!;
  };
};

const TextShortRenderer = ({ data, step, answers, onAnswer }: RendererProps<TextShortData>) => {
  const initial =
    typeof answers[data.answerKey] === "string" ? (answers[data.answerKey] as string) : "";

  const randomize = buildRandomizer(data.randomFromList);

  return (
    <TextShort
      question={data.question}
      description={data.description}
      placeholder={data.placeholder}
      initialValue={initial}
      presets={data.presets}
      randomize={randomize}
      maxLength={data.maxLength}
      onChange={(value) => onAnswer(data.answerKey, { value })}
      testId={`renderer-text-short-${step.id}`}
    />
  );
};

export const textShortRenderer = createRenderer({
  type: "text_short",
  dataSchema: textShortSchema,
  label: "Text short",
  Component: TextShortRenderer,
});

export default textShortRenderer;
